import { BlockDiagram } from '@cadstrom/cadstrom-shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import * as z from 'zod';

import { BlockDiagramControls } from '#components/block-diagram/block-diagram-controls.js';
import { BlockDiagramViewer } from '#components/block-diagram/block-diagram-viewer.js';
import { Loading } from '#components/loading.js';
import { GenericErrorBoundary } from '#screens/error-boundaries/generic-error-boundary.js';
import { useTRPC } from '#src/trpc.js';

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/block-diagram',
)({
	validateSearch: z.object({
		variant: BlockDiagram.BlockDiagramVariantSchema.optional(),
		// Pins the page to one attachment (e.g. followed from a workbench embed);
		// omitted → the revision's latest diagram.
		attachmentId: z.string().optional(),
	}),
	staticData: {
		authorize: (ability) => ability.can('view', 'project'),
	},
	loader: async ({ context: { queryClient, getProjectPreviewOptions } }) => {
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [{ title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • Block Diagram • Cadstrom' }],
	}),
	component: BlockDiagramPage,
	pendingComponent: Loading,
});

function BlockDiagramPage() {
	const { revisionId } = Route.useParams();
	const { variant: requestedVariant, attachmentId: requestedAttachmentId } = Route.useSearch();
	const navigate = Route.useNavigate();
	const trpc = useTRPC();

	const variant = requestedVariant ?? 'full';
	// Knobs stay unset until the user commits them, so the shared defaults (and
	// the document placement's own spacing) apply.
	const [knobs, setKnobs] = useState<BlockDiagram.BlockDiagramKnobs>({});

	// The latest-diagram lookup is only needed when no attachment is pinned.
	const { data: listData, isLoading: listLoading } = useQuery(
		trpc.requirementsV2.attachments.listByBuild.queryOptions(
			{
				buildId: revisionId,
				type: BlockDiagram.BLOCK_DIAGRAM_ATTACHMENT_TYPE,
			},
			{ enabled: requestedAttachmentId === undefined },
		),
	);
	const attachmentId = requestedAttachmentId ?? listData?.attachments[0]?.id;
	const { data: attachmentData, isLoading: documentLoading } = useQuery(
		trpc.requirementsV2.attachments.get.queryOptions(
			{ attachmentId: attachmentId ?? '' },
			{ enabled: attachmentId !== undefined },
		),
	);

	const document = attachmentData?.document;
	const resolvedKnobs = useMemo(() => BlockDiagram.resolveBlockDiagramKnobs(document, knobs), [knobs, document]);

	if ((requestedAttachmentId === undefined && listLoading) || (attachmentId !== undefined && documentLoading)) {
		return <Loading />;
	}

	if (document === undefined) {
		return (
			<div className='flex h-full items-center justify-center p-8 text-sm text-gray-a10'>
				No block diagram has been generated for this revision.
			</div>
		);
	}

	return (
		<div className='flex h-full min-h-0 w-full min-w-0 grow basis-0 flex-col gap-2 p-2'>
			<div className='flex flex-wrap items-center justify-between gap-4'>
				<BlockDiagramControls
					variant={variant}
					onVariantChange={(next) => void navigate({ search: (prev) => ({ ...prev, variant: next }) })}
					knobs={resolvedKnobs}
					onKnobsChange={setKnobs}
				/>
			</div>
			<GenericErrorBoundary>
				<BlockDiagramViewer
					revisionId={revisionId}
					document={document}
					variant={variant}
					knobs={resolvedKnobs}
					className='h-full min-h-0 w-full min-w-0 rounded-lg border border-gray-a4 bg-blue-2'
				/>
			</GenericErrorBoundary>
		</div>
	);
}
